/** Bouton ⓘ + panneau « Source » : titre, éditeur, URL, repère, date ; ou badge TODO / Estimation IA. */
import type { Figure, MeasureStatus, Source } from "../../../engine/src/types.js";
import { FC_VERIFIED, fcBadge, fcBadgeHtml, fcBadgeInner } from "../factcheck.js";
import { escapeHtml as e } from "../format.js";
import { icon } from "../icons.js";

export interface SourceInfo {
  title: string;
  sources: Array<Source | "TODO">;
  status?: MeasureStatus | "simulated" | undefined;
  /** Phrases complémentaires (fourchette, note, moteur). */
  notes?: string[];
  /** `scaled` : chiffre dérivé de la source (règle de trois, contrefactuel) — affiché « dérivé de <source> » (ADR 0004). */
  derivation?: "scaled" | "proposal_envelope" | undefined;
  /** Identifiant du chiffre dans l'inventaire du fact-checker (`measures/<id>/cost`, `baseline/apu/debt_bn`…) : badge. */
  figureId?: string | undefined;
}

const registry = new Map<string, SourceInfo>();
let counter = 0;

/** Enregistre une info source et renvoie le HTML du bouton ⓘ. */
export function srcButton(info: SourceInfo, opts: { inline?: boolean } = {}): string {
  const id = `src${++counter}`;
  registry.set(id, info);
  const todo = info.sources.length === 0 || info.sources.every((s) => s === "TODO");
  const cls = ["src", opts.inline ? "inline" : "", info.status === "estimé_ia" ? "ai" : todo && info.status !== "simulated" ? "todo" : ""].join(" ");
  const label = info.status === "estimé_ia" ? "Estimation IA, non sourcée" : todo ? "Source à documenter" : "Voir la source";
  return `<button type="button" class="${cls}" data-src="${id}" aria-label="${e(label)} : ${e(info.title)}" title="${e(label)}">i</button>${opts.inline && !todo ? fcBadgeHtml(info.figureId, id) : ""}`;
}

export function figureSources(fig: Figure | undefined): Array<Source | "TODO"> {
  if (!fig) return ["TODO"];
  return [fig.source];
}

export function figureNotes(fig: Figure | undefined): string[] {
  const out: string[] = [];
  if (!fig) return ["Valeur absente de la baseline."];
  if (fig.low !== undefined && fig.high !== undefined) out.push(`Fourchette : ${fig.low} – ${fig.high} (paramètre contesté).`);
  if (fig.year !== undefined) out.push(`Année de référence : ${fig.year}.`);
  if (fig.note) out.push(fig.note);
  return out;
}

export function renderSourcePanel(id: string): string {
  const info = registry.get(id);
  if (!info) return "";
  const list = info.sources
    .map((s) => {
      if (s === "TODO") return `<p><span class="badge">TODO / non sourcé</span> <span class="mut sm">Chiffre à documenter (docs/sources-todo.md). Ne pas citer.</span></p>`;
      return `<dl><dt>Document</dt><dd>${e(s.title)}</dd><dt>Éditeur</dt><dd>${e(s.publisher)}</dd>${s.locator ? `<dt>Repère</dt><dd>${e(s.locator)}</dd>` : ""}<dt>URL</dt><dd><a href="${e(s.url)}" target="_blank" rel="noopener noreferrer">${e(s.url)}</a></dd><dt>Consulté le</dt><dd>${e(s.accessed)}</dd>${s.note ? `<dt>Note</dt><dd>${e(s.note)}</dd>` : ""}</dl>`;
    })
    .join("");
  const derived =
    info.derivation === "scaled"
      ? `<p><span class="badge warn">Dérivé</span> <span class="sm">Chiffre <b>dérivé de ${e(info.sources.filter((s): s is Source => s !== "TODO").map((s) => s.publisher).join(", ") || "la source")}</b> par règle de trois ou contrefactuel (détail dans la note ci-dessous) : la source ne publie pas ce chiffre tel quel.</span></p>`
      : info.derivation === "proposal_envelope"
        ? `<p><span class="badge warn">Enveloppe de la proposition</span> <span class="sm">Le montant est l'enveloppe annoncée par la proposition elle-même, pas un chiffrage indépendant.</span></p>`
        : "";
  const statusBadge =
    info.status === "estimé_ia"
      ? `<p><span class="badge ai">Estimation IA — non sourcée</span></p>`
      : info.status === "non_chiffré"
        ? `<p><span class="badge">Non chiffré</span></p>`
        : info.status === "simulated"
          ? `<p><span class="badge mut">Valeur simulée</span></p>`
          : "";
  const fc = fcBadge(info.figureId);
  const fcHtml = fc
    ? `<p class="fcline"><span class="fc fc-${fc.badge}">${fcBadgeInner(fc.badge, fc.label)}</span> <span class="sm mut">${FC_VERIFIED.has(fc.badge) ? "Relu par le fact-checker : chiffre retrouvé dans la source." : fc.badge === "ecart" ? "Le fact-checker a relevé un écart avec la source." : "Pas encore confirmé par le fact-checker."}</span>${fc.comment ? ` <span class="sm mut">${e(fc.comment)}</span>` : ""}</p>`
    : "";
  return `<div class="sheet" role="dialog" aria-modal="true" aria-labelledby="src-title">
    <h2 id="src-title"><span>Source · ${e(info.title)}</span><button type="button" class="ghost close" data-close aria-label="Fermer">${icon("fermer", 20)}</button></h2>
    ${statusBadge}${fcHtml}${derived}${list}${(info.notes ?? []).map((n) => `<p class="sm mut">${e(n)}</p>`).join("")}
  </div>`;
}

export function bindSourcePanel(root: HTMLElement, modal: HTMLElement) {
  root.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-src]");
    if (!btn) return;
    ev.preventDefault();
    const id = btn.dataset.src!;
    modal.innerHTML = renderSourcePanel(id);
    modal.hidden = false;
    modal.querySelector<HTMLElement>("[data-close]")?.focus();
  });
  modal.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    if (t === modal || t.closest("[data-close]")) {
      modal.hidden = true;
      modal.innerHTML = "";
    }
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !modal.hidden) {
      modal.hidden = true;
      modal.innerHTML = "";
    }
  });
}
